import React, {useRef, useState} from 'react'
import PropTypes from 'prop-types'
import {Box, Image, IconButton} from '@salesforce/retail-react-app/app/components/shared/ui'

import {getUrl} from '../../../../../utils/cloudinary-utils'
import getCloudinaryVideoUrl from '../../../../cloudinary-media/video'
import breakpoints from '../../../../../theme/foundations/breakpoints'

const Video = ({
    desktopVideo,
    mobileVideo,
    mobileVideoThumbnail,
    videoAlt,
    videoEnableSound,
    videoDisableAutoPlay,
    variant
}) => {
    const videoRef = useRef(null)
    const [isPlaying, setIsPlaying] = useState(!videoDisableAutoPlay)
    const [isMuted, setIsMuted] = useState(true)

    const sizeDesktop = {
        width: variant === 'promoGridTwoItems' ? 955 : 472,
        height: variant === 'promoGridTwoItems' ? 540 : 428
    }
    const sizeMobile = {
        width: variant === 'promoGridTwoItems' ? 414 : 364,
        height: variant === 'promoGridTwoItems' ? 336 : 386
    }

    const togglePlayPause = (videoRef) => {
        if (videoRef.current) {
            if (isPlaying) {
                videoRef.current.pause()
            } else {
                videoRef.current.play()
            }
            setIsPlaying(!isPlaying)
        }
    }

    const toggleMuteUnmute = (videoRef) => {
        if (videoRef.current) {
            videoRef.current.muted = !isMuted
            setIsMuted(!isMuted)
        }
    }

    return (
        <Box
            position="relative"
            width="100%"
            height="100%"
            sx={{
                video: {
                    aspectRatio: {
                        base: `${sizeMobile.width}/${sizeMobile.height}`,
                        md: `${sizeDesktop.width}/${sizeDesktop.height}`
                    }
                }
            }}
        >
            <video
                ref={videoRef}
                poster={
                    mobileVideoThumbnail
                        ? mobileVideoThumbnail
                        : `${mobileVideo?.replace('mp4', '')}webp`
                }
                {...(isPlaying && {autoPlay: true})}
                muted
                alt={videoAlt}
                loop
                playsInline
                style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover'
                }}
            >
                <source
                    src={
                        mobileVideo?.indexOf('ar_') !== -1
                            ? mobileVideo
                            : getCloudinaryVideoUrl({
                                  src: mobileVideo,
                                  aspectRatio: `${sizeMobile.width}:${sizeMobile.height}`
                              })
                    }
                    media={`(max-width: ${breakpoints.md})`}
                />
                <source
                    src={
                        desktopVideo?.indexOf('ar_') !== -1
                            ? desktopVideo
                            : getCloudinaryVideoUrl({
                                  src: desktopVideo,
                                  aspectRatio: `${sizeDesktop.width}:${sizeDesktop.height}`
                              })
                    }
                />
            </video>

            <Box position="absolute" top="20px" right="20px" gap="10px" display="grid">
                <IconButton
                    onClick={() => togglePlayPause(videoRef)}
                    aria-label="Toggle Play/Pause"
                    icon={
                        <Image
                            width="30px"
                            height="30px"
                            src={
                                isPlaying
                                    ? getUrl('UIImages/images/video-pause-hover.svg')
                                    : getUrl('UIImages/images/video-play-hover.svg')
                            }
                            alt="Pause/ Play Icon"
                        />
                    }
                    variant="unstyled"
                    height="auto"
                    minHeight="auto"
                    minWidth="auto"
                    display="inline-flex"
                    gap="10px"
                    zIndex="2"
                    opacity="0.8"
                    _hover={{
                        opacity: '1'
                    }}
                />

                {videoEnableSound && (
                    <IconButton
                        onClick={() => toggleMuteUnmute(videoRef)}
                        aria-label="Toggle Mute/Unmute"
                        icon={
                            <Image
                                width="30px"
                                height="30px"
                                src={
                                    isMuted
                                        ? getUrl('UIImages/images/mute-hover.svg')
                                        : getUrl('UIImages/images/unmute-hover.svg')
                                }
                                alt="Mute/ Unmute Icon"
                            />
                        }
                        variant="unstyled"
                        height="auto"
                        minHeight="auto"
                        minWidth="auto"
                        gap="10px"
                        display="inline-flex"
                        zIndex="2"
                        opacity="0.8"
                        _hover={{
                            opacity: '1'
                        }}
                    />
                )}
            </Box>
        </Box>
    )
}

Video.propTypes = {
    desktopVideo: PropTypes.string.isRequired,
    mobileVideo: PropTypes.string,
    desktopVideoThumbnail: PropTypes.string,
    mobileVideoThumbnail: PropTypes.string,
    videoAlt: PropTypes.string,
    videoEnableSound: PropTypes.bool,
    videoDisableAutoPlay: PropTypes.bool,
    variant: PropTypes.string,
    mediaSize: PropTypes.string,
    desktopMediaPosition: PropTypes.string,
    mobileMediaPosition: PropTypes.string
}

export default Video
